// AGC: "What's broken" — voice retries, voice errors, wizard abandons, form failures.
import { useEffect, useState } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import { InsightCard } from './InsightCard'
import {
  getVoiceFrictionStats,
  getVoiceErrors,
  getWizardAbandons,
  getFormFailures,
  type DateRange,
  type VoiceFrictionRow,
  type VoiceErrorRow,
  type WizardAbandonRow,
  type FormFailureRow,
} from '../../../services/analyticsQueryService'
import {
  interpretVoiceFriction,
  interpretVoiceErrors,
  interpretWizardAbandons,
  interpretFormFailures,
} from '../../../services/analyticsInsightGenerator'
import { useTranslation } from '../../../hooks/useTranslation'

interface FrictionSectionProps {
  range: DateRange
}

// AGC: Shared skeleton placeholder for loading state
function LoadingBox(): JSX.Element {
  return <div className="h-48 bg-slate-100 dark:bg-white/[0.04] animate-pulse rounded" />
}

// AGC: Shared "good — no data" placeholder for friction sections (absence = success)
function EmptyGood({ message }: { message: string }): JSX.Element {
  return (
    <div className="text-sm text-emerald-600 dark:text-emerald-400 h-48 flex items-center justify-center">
      {message}
    </div>
  )
}

// AGC: Shared "no data yet" placeholder
function EmptyNeutral({ message }: { message: string }): JSX.Element {
  return (
    <div className="text-sm text-slate-500 dark:text-slate-400 h-48 flex items-center justify-center">
      {message}
    </div>
  )
}

export function FrictionSection({ range }: FrictionSectionProps): JSX.Element {
  const [voiceFriction, setVoiceFriction] = useState<VoiceFrictionRow[] | null>(null)
  const [voiceErrors, setVoiceErrors] = useState<VoiceErrorRow[] | null>(null)
  const [wizards, setWizards] = useState<WizardAbandonRow[] | null>(null)
  const [forms, setForms] = useState<FormFailureRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()

  useEffect(() => {
    // AGC: Cancel flag prevents setState on unmounted component
    let cancelled = false
    setLoading(true)
    Promise.all([
      getVoiceFrictionStats(range),
      getVoiceErrors(range),
      getWizardAbandons(range),
      getFormFailures(range),
    ])
      .then(([vf, ve, w, f]) => {
        if (cancelled) return
        setVoiceFriction(vf)
        setVoiceErrors(ve)
        setWizards(w)
        setForms(f)
      })
      .catch((err) => { console.warn('[analytics-dashboard] friction failed', err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [range])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* AGC: Voice retry rate table — per entity_type with color-coded retry rate column */}
      <InsightCard
        title={t('admin.analytics.card.voiceFriction')}
        insight={voiceFriction ? interpretVoiceFriction(voiceFriction) : undefined}
        helpDescription="For each entity type, what % of voice attempts need a retry before succeeding."
        helpGoodSignal="Under 15% retry rate."
        helpBadSignal="Over 30% — users have to repeat themselves constantly."
        helpAction="Focus on the worst entity first — update voice prompts or backend parse rules."
      >
        {loading || !voiceFriction ? (
          <LoadingBox />
        ) : voiceFriction.length === 0 ? (
          <EmptyNeutral message={t('admin.analytics.empty.noVoice')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/5">
                  <th className="text-left py-2 pr-2">{t('admin.analytics.table.entity')}</th>
                  <th className="text-right py-2 px-2">{t('admin.analytics.table.firstTry')}</th>
                  <th className="text-right py-2 px-2">{t('admin.analytics.table.retries')}</th>
                  <th className="text-right py-2 px-2">{t('admin.analytics.table.fails')}</th>
                  <th className="text-right py-2 pl-2">{t('admin.analytics.table.retryRate')}</th>
                </tr>
              </thead>
              <tbody>
                {voiceFriction.map((r) => {
                  // AGC: Retry rate color thresholds: >30% red, >15% amber, else emerald
                  const rateColor =
                    r.retryRatePct > 30
                      ? 'text-red-600 dark:text-red-400'
                      : r.retryRatePct > 15
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-emerald-600 dark:text-emerald-400'
                  return (
                    <tr key={r.entity_type} className="border-b border-slate-100 dark:border-white/5">
                      <td className="py-2 pr-2 font-medium text-slate-900 dark:text-slate-100">{r.entity_type}</td>
                      <td className="py-2 px-2 text-right text-slate-600 dark:text-slate-300">{r.firstTrySuccess}</td>
                      <td className="py-2 px-2 text-right text-slate-600 dark:text-slate-300">{r.retries}</td>
                      <td className="py-2 px-2 text-right text-slate-600 dark:text-slate-300">{r.failures}</td>
                      <td className={`py-2 pl-2 text-right font-semibold ${rateColor}`}>
                        {r.retryRatePct}%
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </InsightCard>

      {/* AGC: Voice errors bar chart — X = error_code, Y = count, bars in red */}
      <InsightCard
        title={t('admin.analytics.card.voiceErrors')}
        insight={voiceErrors ? interpretVoiceErrors(voiceErrors) : undefined}
        helpDescription="Which error codes users hit when voice fails."
        helpAction="mic_denied → fix onboarding. parse_failed → update voice prompts."
      >
        {loading || !voiceErrors ? (
          <LoadingBox />
        ) : voiceErrors.length === 0 ? (
          <EmptyGood message={t('admin.analytics.empty.noVoiceErrors')} />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={voiceErrors} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="error_code" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#ef4444" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </InsightCard>

      {/* AGC: Wizard abandon table — wizard name, last step seen, count of drop-offs */}
      <InsightCard
        title={t('admin.analytics.card.wizardAbandons')}
        insight={wizards ? interpretWizardAbandons(wizards) : undefined}
        helpDescription="Where users abandon a wizard flow. Shows which wizard + which last-viewed step."
        helpAction="Redesign the step that loses the most users."
      >
        {loading || !wizards ? (
          <LoadingBox />
        ) : wizards.length === 0 ? (
          <EmptyGood message={t('admin.analytics.empty.noWizardAbandons')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/5">
                  <th className="text-left py-2 pr-2">{t('admin.analytics.table.wizard')}</th>
                  <th className="text-left py-2 px-2">{t('admin.analytics.table.lastStep')}</th>
                  <th className="text-right py-2 pl-2">{t('admin.analytics.table.lost')}</th>
                </tr>
              </thead>
              <tbody>
                {wizards.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-white/5">
                    <td className="py-2 pr-2 font-medium text-slate-900 dark:text-slate-100">{r.wizard_name}</td>
                    <td className="py-2 px-2 text-slate-600 dark:text-slate-300">{r.last_step_name}</td>
                    <td className="py-2 pl-2 text-right text-amber-600 dark:text-amber-400 font-semibold">
                      {r.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </InsightCard>

      {/* AGC: Form failures table — form name, error code, count; count in red */}
      <InsightCard
        title={t('admin.analytics.card.formFailures')}
        insight={forms ? interpretFormFailures(forms) : undefined}
        helpDescription="Which forms fail most often and why (validation, network, permission, duplicate, unknown)."
        helpAction="validation → confusing form. network → backend issue. permission → Firestore rules."
      >
        {loading || !forms ? (
          <LoadingBox />
        ) : forms.length === 0 ? (
          <EmptyGood message={t('admin.analytics.empty.noFormFailures')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/5">
                  <th className="text-left py-2 pr-2">{t('admin.analytics.table.form')}</th>
                  <th className="text-left py-2 px-2">{t('admin.analytics.table.error')}</th>
                  <th className="text-right py-2 pl-2">{t('admin.analytics.table.count')}</th>
                </tr>
              </thead>
              <tbody>
                {forms.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-white/5">
                    <td className="py-2 pr-2 font-medium text-slate-900 dark:text-slate-100">{r.form_name}</td>
                    <td className="py-2 px-2 text-slate-600 dark:text-slate-300">{r.error_code}</td>
                    <td className="py-2 pl-2 text-right text-red-600 dark:text-red-400 font-semibold">
                      {r.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </InsightCard>
    </div>
  )
}
